import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, AlertTriangle, ExternalLink, History, X } from 'lucide-react';
import { Button, EmptyState, Modal, Spinner } from '../ui';
import { useAuth } from '../../auth/useAuth';
import { fetchProductSpotHistory } from '../../services/dataService';
import { formatCount } from '../production/chartTheme';
import SpotHistoryFilters from './SpotHistoryFilters';
import SpotHistoryGrid from './SpotHistoryGrid';
import SpotDetailPanel from './SpotDetailPanel';
import {
  ALL_RETAILERS,
  DEFAULT_HISTORY_RANGE,
  buildSpotHistoryGrid,
  initialRetailerSelection,
  resolveHistoryRange,
  retailerOptions,
} from './spotHistoryModel';

/**
 * Tableau view this modal replaces. Kept as a secondary link while the native
 * view is validated against it; remove once Tableau is retired.
 */
function buildTableauUrl(upc, retailer) {
  const params = new URLSearchParams();
  if (retailer !== ALL_RETAILERS) params.set('Customername', retailer);
  params.set('SelectedProduct', upc);
  return `https://us-east-1.online.tableau.com/#/site/reesa/views/BuscadorREESA2025_4/HistorialParticipaciondelProducto?${params.toString()}`;
}

/**
 * "View Product History" from ProductsManager — native replacement for the
 * Tableau `HistorialParticipaciondelProducto` view.
 *
 * Shows every spot the product appeared in (columns) and every product that
 * shared those spots (rows). Clicking a spot opens its image and SW1 link.
 *
 * The API returns all retailers for the date range; the retailer filter is
 * client-side so switching it is instant. Changing the range refetches.
 */
export default function ProductSpotHistoryModal({
  product,
  initialRetailer,
  onClose,
  usedUpcs,
  onCopyUpc,
  onRequestReEnable,
  suspendEscape = false,
}) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const upc = String(product.upc);

  const [range, setRange] = useState(DEFAULT_HISTORY_RANGE);
  const [retailer, setRetailer] = useState(() => initialRetailerSelection(initialRetailer));
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedSpotId, setSelectedSpotId] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  useEffect(() => {
    if (authLoading || !isAuthenticated) return undefined; // see CLAUDE.md auth race

    const controller = new AbortController();
    const { from, to } = resolveHistoryRange(range);
    setStatus('loading');

    fetchProductSpotHistory({ upc, from, to, signal: controller.signal })
      .then((result) => {
        if (result === null) {
          setStatus('error');
          return;
        }
        setData(result);
        setStatus('ready');
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') setStatus('error');
      });

    return () => controller.abort();
  }, [upc, range, reloadKey, authLoading, isAuthenticated]);

  const grid = useMemo(
    () => buildSpotHistoryGrid(data, { retailer, selectedUpc: upc }),
    [data, retailer, upc]
  );
  const retailers = useMemo(() => retailerOptions(data?.spots, retailer), [data, retailer]);

  const selectedSpot = useMemo(
    () => grid.columns.find((c) => c.spotId === selectedSpotId) || null,
    [grid.columns, selectedSpotId]
  );
  const productsInSelectedSpot = useMemo(() => {
    if (!selectedSpot || !data) return 0;
    return data.products.filter((p) => p.spotIds.includes(selectedSpot.spotId)).length;
  }, [data, selectedSpot]);

  // Esc peels one layer at a time: lightbox → spot panel → modal. Handled here,
  // on window in capture phase, instead of relying on the Modal's own handler:
  // that one only fires while focus is inside the panel, and a refetch unmounts
  // the grid (dropping focus to <body>), after which Esc would do nothing.
  // `suspendEscape`: a dialog stacked on top (ProductsManager's re-enable
  // confirmation) is open, so Esc is swallowed — it must not close this modal
  // from under it (the Modal's own Esc handler would, if we let it through).
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      if (suspendEscape) return;
      if (lightboxUrl) setLightboxUrl(null);
      else if (selectedSpot) setSelectedSpotId(null);
      else onClose();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [lightboxUrl, selectedSpot, onClose, suspendEscape]);

  const handleSelectSpot = useCallback((spotId) => {
    setSelectedSpotId((current) => (current === spotId ? null : spotId));
  }, []);

  const description = product.description || data?.description || '';

  const title = (
    <span className="flex flex-col">
      <span>Participación Histórica (Spots)</span>
      <span className="text-sm font-normal text-neutral-500">
        {description && <>{description} · </>}
        <span className="tabular-nums">{upc}</span>
      </span>
    </span>
  );

  return (
    <Modal open onClose={onClose} title={title} size="full">
      <div className="flex-1 min-h-0 flex flex-col gap-3 overflow-y-auto lg:overflow-visible">
        <SpotHistoryFilters
          retailers={retailers}
          totalSpots={data?.spots.length ?? 0}
          retailer={retailer}
          onRetailerChange={setRetailer}
          range={range}
          onRangeChange={setRange}
          disabled={status !== 'ready'}
        />

        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-neutral-500">
          <span>
            {status === 'ready' && (
              <>
                {formatCount(grid.columns.length)} spots · {formatCount(grid.rows.length)} products
              </>
            )}
          </span>
          <a
            href={buildTableauUrl(upc, retailer)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 hover:text-neutral-700"
          >
            Open in Tableau <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        {status === 'ready' && data?.truncated && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-warning-50 text-warning-700 border border-warning-100 text-xs">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            This product has more spots than can be shown at once. Only the most recent are listed — narrow the start date to see a complete range.
          </div>
        )}

        <div className="flex-1 min-h-0 flex flex-col lg:flex-row gap-3">
          {status === 'loading' && (
            <div className="flex-1 flex items-center justify-center">
              <Spinner />
            </div>
          )}

          {status === 'error' && (
            <EmptyState
              className="flex-1"
              icon={<AlertCircle className="w-6 h-6" />}
              title="Couldn't load the product history"
              description="The spot history service didn't respond. Try again in a moment."
              action={<Button variant="secondary" onClick={() => setReloadKey((k) => k + 1)}>Retry</Button>}
            />
          )}

          {status === 'ready' && grid.columns.length === 0 && (
            <EmptyState
              className="flex-1"
              icon={<History className="w-6 h-6" />}
              title="No spots found"
              description={
                retailer === ALL_RETAILERS
                  ? 'This product has no spots in the selected date range.'
                  : `This product has no ${retailer} spots in the selected date range.`
              }
              action={retailer !== ALL_RETAILERS && data?.spots.length > 0 && (
                <Button variant="secondary" onClick={() => setRetailer(ALL_RETAILERS)}>
                  Show all retailers
                </Button>
              )}
            />
          )}

          {status === 'ready' && grid.columns.length > 0 && (
            <>
              <SpotHistoryGrid
                columns={grid.columns}
                rows={grid.rows}
                selectedSpotId={selectedSpot?.spotId ?? null}
                onSelectSpot={handleSelectSpot}
                usedUpcs={usedUpcs}
                onCopyUpc={onCopyUpc}
                onRequestReEnable={onRequestReEnable}
              />
              {selectedSpot && (
                <SpotDetailPanel
                  key={selectedSpot.spotId}
                  spot={selectedSpot}
                  productsInSpot={productsInSelectedSpot}
                  onClose={() => setSelectedSpotId(null)}
                  onOpenImage={setLightboxUrl}
                />
              )}
            </>
          )}
        </div>

        {/* `fixed` resolves against the Modal panel (it has a CSS transform), so
            this covers exactly the modal, wherever the body is scrolled. */}
        {lightboxUrl && (
          <div
            className="fixed inset-0 z-50 bg-neutral-900/85 rounded-lg flex items-center justify-center p-4"
            onClick={() => setLightboxUrl(null)}
            role="dialog"
            aria-label="Spot image"
          >
            <img
              src={lightboxUrl}
              alt="Spot"
              className="max-w-full max-h-full object-contain rounded shadow-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute top-3 right-3 flex items-center gap-2">
              <a
                href={lightboxUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-white/90 text-neutral-800 text-xs font-medium hover:bg-white"
              >
                Open in new tab <ExternalLink className="w-3 h-3" />
              </a>
              <button
                type="button"
                onClick={() => setLightboxUrl(null)}
                className="p-1.5 rounded-md bg-white/90 text-neutral-800 hover:bg-white"
                aria-label="Close image"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
